import { useMemo, useCallback } from 'react';
import { SELECTION_EFFECTS } from '../constants/colors';

interface SelectionOptions {
  selectedColor?: string;
  selectedStroke?: string;
  selectedStrokeWidth?: number;
  selectedOpacity?: number;
  unselectedOpacity?: number;
  normalOpacity?: number;
  selectedSize?: number;
  normalSize?: number;
}

interface ElementStyle {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  r?: number; // for circles
  cursor?: string;
}

/**
 * D3.js要素の選択状態を管理するカスタムフック
 * 
 * @param selectedIndices 選択されている要素のインデックス配列
 * @param options 選択時のスタイルオプション
 * @returns 選択状態の判定とスタイル取得関数
 * 
 * @example
 * ```typescript
 * const selection = useD3Selection(selectedRows, {
 *   selectedStroke: '#ff0000',
 *   selectedOpacity: 1
 * });
 * 
 * // D3要素のスタイル設定で使用
 * circles
 *   .style('opacity', d => selection.getOpacity(d.index))
 *   .style('stroke', d => selection.getStroke(d.index))
 *   .attr('r', d => selection.getSize(d.index));
 * ```
 */
export function useD3Selection(
  selectedIndices: number[] = [],
  options: SelectionOptions = {}
) {
  const {
    selectedColor,
    selectedOpacity = SELECTION_EFFECTS.selectedOpacity,
    unselectedOpacity = SELECTION_EFFECTS.unselectedOpacity,
    normalOpacity = SELECTION_EFFECTS.normalOpacity,
    normalSize = 4
  } = options;

  // 選択セットを作成（高速な検索のため）
  const selectedSet = useMemo(() => {
    return new Set(selectedIndices);
  }, [selectedIndices]);

  // 選択があるかどうか
  const hasSelection = selectedIndices.length > 0;

  // 要素が選択されているかどうかを判定
  const isSelected = useCallback((index: number | number[]): boolean => {
    if (Array.isArray(index)) {
      // 配列の場合、全て選択されているかチェック
      return index.length > 0 && index.every(idx => selectedSet.has(idx));
    }
    return selectedSet.has(index);
  }, [selectedSet]);

  // 部分選択かどうかを判定（配列の一部だけが選択されている）
  const isPartiallySelected = useCallback((indices: number[]): boolean => {
    if (indices.length === 0) return false;
    const selectedCount = indices.filter(idx => selectedSet.has(idx)).length;
    return selectedCount > 0 && selectedCount < indices.length;
  }, [selectedSet]);

  // 選択率を取得（0.0〜1.0）
  const getSelectionRatio = useCallback((indices: number[]): number => {
    if (indices.length === 0) return 0;
    const selectedCount = indices.filter(idx => selectedSet.has(idx)).length;
    return selectedCount / indices.length;
  }, [selectedSet]);

  // 不透明度を取得
  const getOpacity = useCallback((index: number | number[]): number => {
    if (!hasSelection) return normalOpacity;
    const selected = isSelected(index);
    return selected ? selectedOpacity : unselectedOpacity;
  }, [hasSelection, isSelected, normalOpacity, selectedOpacity, unselectedOpacity]);

  // ストローク色を取得
  const getStroke = useCallback((_index: number | number[], defaultStroke = 'none'): string => {
    // 選択されていても、strokeは返さない（黒い枠を表示しない）
    return defaultStroke;
  }, []);

  // ストローク幅を取得
  const getStrokeWidth = useCallback((_index: number | number[], defaultWidth = 0): number => {
    // 選択されていても、stroke幅は変更しない（黒い枠を表示しない）
    return defaultWidth;
  }, []);

  // 塗りつぶし色を取得
  const getFill = useCallback((index: number | number[], defaultFill: string): string => {
    if (!selectedColor) return defaultFill;
    const selected = isSelected(index);
    return selected ? selectedColor : defaultFill;
  }, [isSelected, selectedColor]);

  // サイズを取得（円などの場合）
  const getSize = useCallback((_index: number | number[]): number => {
    // 選択されていても、サイズは変更しない
    return normalSize;
  }, [normalSize]);

  // 完全なスタイルオブジェクトを取得
  const getStyle = useCallback((
    index: number | number[],
    defaultStyle: Partial<ElementStyle> = {}
  ): ElementStyle => {
    const selected = isSelected(index);
    
    return {
      fill: selectedColor && selected ? selectedColor : defaultStyle.fill,
      stroke: defaultStyle.stroke || 'none',  // 選択時も黒い枠を表示しない
      strokeWidth: defaultStyle.strokeWidth || 0,  // 選択時も枠幅を変更しない
      opacity: getOpacity(index),
      cursor: defaultStyle.cursor || 'pointer'
    };
  }, [isSelected, selectedColor, getOpacity]);

  // ホバー時のスタイルを取得
  const getHoverStyle = useCallback((
    index: number | number[],
    isHovering: boolean
  ): Partial<ElementStyle> => {
    if (!isHovering) {
      return {
        opacity: getOpacity(index)
      };
    }
    
    // ホバー時は常に完全不透明
    return {
      opacity: 1
    };
  }, [getOpacity]);

  // クリックアクションのヒントテキストを取得
  const getClickHint = useCallback((indices: number[]): string => {
    if (indices.length === 0) return '';
    
    const selectedCount = indices.filter(idx => selectedSet.has(idx)).length;
    const allSelected = selectedCount === indices.length;
    const someSelected = selectedCount > 0;
    
    if (allSelected) {
      return 'Cmd+クリック: 選択解除';
    } else if (someSelected) {
      return 'Cmd+クリック: 追加選択';
    } else {
      return 'クリック: 選択';
    }
  }, [selectedSet]);

  return {
    // 状態
    hasSelection,
    selectedIndices,
    selectedSet,

    // 判定関数
    isSelected,
    isPartiallySelected,
    getSelectionRatio,

    // スタイル取得関数
    getOpacity,
    getStroke,
    getStrokeWidth,
    getFill,
    getSize,
    getStyle,
    getHoverStyle,

    // ユーティリティ
    getClickHint
  };
}

// 戻り値の型をエクスポート
export type D3SelectionHook = ReturnType<typeof useD3Selection>;